// DeleteSpaceServiceModal.tsx — Co Marca — Eliminar Espacio de Servicio
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X, AlertTriangle, Package, Store } from 'lucide-react';
import { deleteEspacioServicio, EspacioServicio } from '../../services/espacioServiciosService';
import { fetchEspacios, fetchServicios, CatalogoItem } from '../../services/catalogosService';

interface DeleteSpaceServiceModalProps {
    isOpen: boolean; onClose: () => void; onSuccess: () => void; servicio: EspacioServicio|null;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.dssm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes dssmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes dssmSlideUp{from{opacity:0;transform:translateY(20px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.dssm-overlay{animation:dssmFadeIn .2s ease both;}
.dssm-dialog{animation:dssmSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.dssm-btn-delete{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(193, 52, 139,.28);transition:all .22s;flex:1;justify-content:center;}
.dssm-btn-delete:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(193, 52, 139,.36);}
.dssm-btn-delete:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.dssm-btn-cancel{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;flex:1;justify-content:center;}
.dssm-btn-cancel:hover{background:#f1f5f9;}
`;

const DeleteSpaceServiceModal: React.FC<DeleteSpaceServiceModalProps> = ({ isOpen, onClose, onSuccess, servicio }) => {
    const [espaciosList, setEspaciosList] = useState<CatalogoItem[]>([]);
    const [serviciosList, setServiciosList] = useState<CatalogoItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError]         = useState<string|null>(null);

    useEffect(() => {
        if (!isOpen || !servicio) return;
        setError(null);
        fetchEspacios().then(setEspaciosList).catch(()=>setEspaciosList([]));
        fetchServicios().then(setServiciosList).catch(()=>setServiciosList([]));
    }, [isOpen, servicio]);

    const getNombre = (list: CatalogoItem[], id: number) => list.find(i=>i.id===id)?.nombre||id.toString();

    const handleDelete = async () => {
        if (!servicio) return;
        setError(null); setIsLoading(true);
        try { await deleteEspacioServicio(servicio.id); onSuccess(); onClose(); }
        catch { setError('Error al eliminar. Intenta de nuevo.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen || !servicio) return null;

    const espacioNombre  = getNombre(espaciosList, servicio.espacio);
    const servicioNombre = getNombre(serviciosList, servicio.servicio);

    return createPortal(
        <>
            <style>{css}</style>
            <div className="dssm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="dssm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.6)', backdropFilter:'blur(6px)' }}/>
                <div className="dssm-dialog" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'460px', boxShadow:'0 32px 80px rgba(0,0,0,.2)', border:'1.5px solid #f1f5f9', overflow:'hidden' }}>
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.primaryDark},${colors.primary}80)` }}/>
                    <div style={{ padding:'24px 24px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            <div style={{ width:'44px', height:'44px', borderRadius:'14px', background:`${colors.primary}12`, border:`1.5px solid ${colors.primary}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                <AlertTriangle size={20} color={colors.primary}/>
                            </div>
                            <div>
                                <h3 style={{ fontSize:'1.05rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Confirmar eliminación</h3>
                                <p style={{ fontSize:'.72rem', color:'#94a3b8', marginTop:'2px' }}>Esta acción no se puede deshacer</p>
                            </div>
                        </div>
                        <button onClick={onClose} disabled={isLoading} style={{ width:'30px', height:'30px', borderRadius:'9px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={14} color="#64748b"/>
                        </button>
                    </div>
                    <div style={{ padding:'20px 24px 24px' }}>
                        <div style={{ display:'flex', gap:'4px', marginBottom:'16px' }}>
                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                <div key={i} style={{ height:'2px', width:i===0?'16px':'5px', borderRadius:'2px', background:c, opacity:.4 }}/>
                            ))}
                        </div>

                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px', marginBottom:'14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        <p style={{ fontSize:'.9rem', color:'#475569', lineHeight:1.65, marginBottom:'14px' }}>
                            ¿Estás seguro que deseas eliminar esta asociación?
                        </p>

                        {/* Cards de lo que se eliminará */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' }}>
                            <div style={{ background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}18`, borderRadius:'12px', padding:'10px 12px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'4px' }}>
                                    <Package size={11} color={colors.primary}/>
                                    <span style={{ fontSize:'.65rem', fontWeight:'800', color:colors.primaryDark, textTransform:'uppercase', letterSpacing:'.06em' }}>Servicio</span>
                                </div>
                                <p style={{ fontSize:'.82rem', fontWeight:'700', color:'#0f172a' }}>{servicioNombre}</p>
                            </div>
                            <div style={{ background:`${colors.secondary}08`, border:`1.5px solid ${colors.secondary}18`, borderRadius:'12px', padding:'10px 12px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'4px' }}>
                                    <Store size={11} color={colors.secondary}/>
                                    <span style={{ fontSize:'.65rem', fontWeight:'800', color:colors.secondary, textTransform:'uppercase', letterSpacing:'.06em' }}>Espacio</span>
                                </div>
                                <p style={{ fontSize:'.82rem', fontWeight:'700', color:'#0f172a' }}>{espacioNombre}</p>
                            </div>
                        </div>

                        <div style={{ background:`${colors.primary}07`, border:`1.5px solid ${colors.primary}18`, borderRadius:'12px', padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:'10px', marginBottom:'22px' }}>
                            <Trash2 size={14} color={colors.primary} style={{ flexShrink:0, marginTop:'1px' }}/>
                            <p style={{ fontSize:'.78rem', color:colors.primaryDark, fontWeight:'600', lineHeight:1.5 }}>
                                Se eliminará permanentemente la asociación entre este espacio y servicio.
                            </p>
                        </div>

                        <div style={{ display:'flex', gap:'10px' }}>
                            <button onClick={onClose} disabled={isLoading} className="dssm-btn-cancel">Cancelar</button>
                            <button onClick={handleDelete} disabled={isLoading} className="dssm-btn-delete">
                                {isLoading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Eliminando...</> : <><Trash2 size={14}/> Eliminar</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default DeleteSpaceServiceModal;